import { DateTime } from 'luxon';
import { StyleSheet } from 'react-native-unistyles';
import type { getRelativeTimeInfo } from '@/components/Event/EventList.utils';
import Text from '@/components/UI/Text';
import { type FragmentOf, graphql, readFragment } from '@/gql/graphql';
import Box from '../UI/Box';
import {
  EventClockFragment,
  useEventClock,
} from './EventClock/EventClock.utils';

export const EventListItemTimeFragment = graphql(/* GraphQL */ `
  fragment EventListItemTime on event {
    id
    datetime
    status
    event_clock {
      event_id
      event_segment {
        id
        title
        abbreviation
      }
    }
    ...EventClock
  }
`, [EventClockFragment]);

export default function EventListItemTime({
  fragment,
  relativeTimeInfo,
}: {
  fragment: FragmentOf<typeof EventListItemTimeFragment> | null | undefined;
  relativeTimeInfo: ReturnType<typeof getRelativeTimeInfo>;
}) {
  const listItemData = readFragment(EventListItemTimeFragment, fragment);
  const clockData = readFragment(EventClockFragment, listItemData);
  const dateObj = DateTime.fromISO(listItemData?.datetime || '');

  const clock = useEventClock({
    fragment: listItemData,
    isDisabledExternal: true,
  });

  const segmentLabel =
    listItemData?.event_clock?.event_segment?.abbreviation ??
    listItemData?.event_clock?.event_segment?.title;

  return (
    <Box center style={style.innerContainer}>
      {clockData?.event_clock ? (
        <>
          <Text style={style.timeSensitiveText(relativeTimeInfo)}>
            {segmentLabel}
          </Text>
          <Text style={style.timeSensitiveText(relativeTimeInfo)}>
            {clock.displayMinutes}:{clock.displaySeconds}
          </Text>
        </>
      ) : listItemData?.datetime ? (
        <Box alignItems="center">
          <Box direction="horizontal" space="sm">
            <Text style={style.timeSensitiveText(relativeTimeInfo)}>
              {dateObj.toFormat('EEE').toUpperCase()}
            </Text>
            <Text style={style.timeSensitiveText(relativeTimeInfo)}>
              {dateObj.toFormat('LLL d').toUpperCase()}
            </Text>
          </Box>
          <Box>
            <Text style={style.timeSensitiveText(relativeTimeInfo)}>
              {dateObj.toFormat('h:mm')} {dateObj.toFormat('a')}
            </Text>
          </Box>
        </Box>
      ) : null}
    </Box>
  );
}

const style = StyleSheet.create((theme) => ({
  text: {
    textAlign: 'center',
    fontWeight: theme.fontWeight.bold,
  },
  innerContainer: {
    borderBottomRightRadius: theme.borderRadius.xl,
    borderRightWidth: theme.size(0.5),
    borderRightColor: theme.colors.surfaceMuted,
    borderBottomWidth: theme.size(0.5),
    borderBottomColor: theme.colors.surfaceMuted,
    minWidth: theme.size(32),
    padding: theme.size(2),
  },
  clockText: {
    color: theme.colors.typography,
    fontWeight: theme.fontWeight.bold,
  },
  timeSensitiveText: (
    relativeTimeInfo: ReturnType<typeof getRelativeTimeInfo>,
  ) => ({
    color: theme.colors[relativeTimeInfo?.variant || 'typography'],
    fontWeight: theme.fontWeight.semibold,
  }),
}));
